import { Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { PlaceBanBadge } from "@/components/bookings/PlaceBanNotice";
import { countNightsLocal, formatIsoDateShort } from "@/lib/bookingDates";
import { displayBookingNotes } from "@/lib/inquiryDisplay";
import { formatStayAmount } from "@/lib/money";
import { isPitchFixed, pitchAssignmentLabel } from "@/lib/pitchAssignment";
import { resolveGuestName } from "@/lib/guestDisplay";
import { useGuestById } from "@/lib/useGuestById";
import { StayCalendarNotes } from "@/components/bookings/StayCalendarNotes";
import type { Booking, Pitch, PlannerCustomEntry } from "@/types/camping";

export interface PlannerSelectionPanelProps {
  booking: Booking | null;
  pitch: Pitch | null;
  customEntry: PlannerCustomEntry | null;
  canEdit: boolean;
  onClose: () => void;
  onEditStay: () => void;
  onShowStayDetails: () => void;
  onEditCustomEntry: () => void;
}

function Detail({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">{label}</dt>
      <dd className="mt-0.5 text-sm text-label">{value}</dd>
    </div>
  );
}

function partyLabel(booking: Booking): string {
  const parts = [`${booking.adults ?? 2} Erw.`];
  if ((booking.children ?? 0) > 0) parts.push(`${booking.children} Kind.`);
  if ((booking.dogs ?? 0) > 0) {
    const dogs = booking.dogs ?? 0;
    parts.push(`${dogs} Hund${dogs > 1 ? "e" : ""}`);
  }
  return parts.join(" · ");
}

export function PlannerSelectionPanel({
  booking,
  pitch,
  customEntry,
  canEdit,
  onClose,
  onEditStay,
  onShowStayDetails,
  onEditCustomEntry,
}: PlannerSelectionPanelProps) {
  const guest = useGuestById(booking?.guest_id);
  if (!booking && !customEntry) return null;

  if (customEntry) {
    const nights = countNightsLocal(customEntry.start_date, customEntry.end_date);
    return (
      <aside
        data-planner-dismiss=""
        className="planner-inspector flex min-h-0 flex-col overflow-hidden rounded-2xl border border-separator bg-card shadow-ios-md max-lg:fixed max-lg:inset-x-3 max-lg:z-[45] max-lg:max-h-[min(24rem,55dvh)] lg:w-[22rem] lg:shrink-0"
        aria-label="Eintragsdetails"
      >
        <header className="flex items-start justify-between gap-3 border-b border-separator px-4 py-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-label-tertiary">
              Eigener Eintrag
            </p>
            <h2 className="truncate text-lg font-semibold tracking-tight text-label">{customEntry.title}</h2>
            <p className="planner-inspector__dates">
              {formatIsoDateShort(customEntry.start_date)} – {formatIsoDateShort(customEntry.end_date)}
              {nights > 0 ? ` · ${nights} ${nights === 1 ? "Nacht" : "Nächte"}` : ""}
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg p-1.5 text-label-secondary hover:bg-fill-tertiary hover:text-label"
            aria-label="Details schließen"
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </header>
        <dl className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: customEntry.color ?? "var(--eh-accent)" }}
              aria-hidden
            />
            <span className="text-sm text-label-secondary">{customEntry.label}</span>
          </div>
          <Detail label="Notiz" value={customEntry.notes} />
        </dl>
        {canEdit ? (
          <footer className="border-t border-separator px-4 py-3">
            <Button type="button" className="w-full" leftIcon={<Pencil size={16} />} onClick={onEditCustomEntry}>
              Eintrag bearbeiten
            </Button>
          </footer>
        ) : null}
      </aside>
    );
  }

  if (!booking) return null;

  const guestName = guest
    ? `${guest.last_name}, ${guest.first_name}`
    : resolveGuestName(booking.guest_id, { booking });
  const pitchLabel = pitch
    ? `${pitch.code}${pitch.label ? ` · ${pitch.label}` : ""}`
    : null;
  const licensePlate = [booking.license_plate, booking.license_plate_country].filter(Boolean).join(" · ");
  const notes = displayBookingNotes(booking.notes);
  const fixed = isPitchFixed(booking.pitch_assignment_mode);

  return (
    <aside
      data-planner-dismiss=""
      className="planner-inspector flex min-h-0 flex-col overflow-hidden rounded-2xl border border-separator bg-card shadow-ios-md max-lg:fixed max-lg:inset-x-3 max-lg:z-[45] max-lg:max-h-[min(28rem,62dvh)] lg:w-[22rem] lg:shrink-0"
      aria-label="Aufenthaltsdetails"
    >
      <header className="flex items-start justify-between gap-3 border-b border-separator px-4 py-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-label-tertiary">
            Aufenthalt
            <span className="ml-2 font-semibold normal-case tracking-normal text-accent">
              {booking.booking_number}
            </span>
          </p>
          <h2 className="truncate text-lg font-semibold tracking-tight text-label">{guestName}</h2>
          <p className="planner-inspector__dates">
            {formatIsoDateShort(booking.arrival_date)} – {formatIsoDateShort(booking.departure_date)}
            {" · "}
            {booking.nights} {booking.nights === 1 ? "Nacht" : "Nächte"}
          </p>
          <StayCalendarNotes arrivalDate={booking.arrival_date} departureDate={booking.departure_date} />
        </div>
        <button
          type="button"
          className="rounded-lg p-1.5 text-label-secondary hover:bg-fill-tertiary hover:text-label"
          aria-label="Details schließen"
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </header>
      <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={booking.status} />
          {booking.place_ban_flagged ? <PlaceBanBadge /> : null}
          {fixed ? (
            <span className="rounded-full bg-fill-tertiary px-2.5 py-0.5 text-xs font-semibold text-label">
              Fester Platz
            </span>
          ) : null}
        </div>
        <dl className="space-y-3">
          <Detail label="Platz" value={pitchLabel ?? pitchAssignmentLabel(booking.pitch_assignment_mode)} />
          <Detail
            label="Kategorie"
            value={pitch?.category_name ?? null}
          />
          <Detail label="Personen" value={partyLabel(booking)} />
          <Detail label="Kennzeichen" value={licensePlate || null} />
          <Detail label="Telefon" value={guest?.phone} />
          <Detail label="E-Mail" value={guest?.email} />
          <Detail label="Betrag" value={formatStayAmount(booking.total_amount, booking.currency, booking.price_pending)} />
          <Detail label="Notiz" value={notes} />
        </dl>
      </div>
      <footer className="flex flex-col gap-2 border-t border-separator px-4 py-3">
        {canEdit ? (
          <Button type="button" className="w-full" leftIcon={<Pencil size={16} />} onClick={onEditStay}>
            Aufenthalt bearbeiten
          </Button>
        ) : null}
        <Button type="button" variant="secondary" className="w-full" onClick={onShowStayDetails}>
          Alle Details
        </Button>
      </footer>
    </aside>
  );
}
